import { Button, Collapse, Container, Grid2 as Grid } from "@mui/material"
import { Link } from "react-router-dom"
import {
    useFetchNonAcquisitionProject,
    useFindAllNonAcquisitionProjects,
    useInitializeAcquisitionProject,
} from "../../api/ProjectsApi"
import { NexAlert } from "../../components/Alert/Alert"
import { Detail, DetailsList } from "../../components/DetailsList/DetailsList"
import { NexAutocomplete } from "../../components/Fields/Autocomplete"
import { LoadingButton } from "../../components/LoadingButton/LoadingButton"
import { Label, Title } from "../../components/Typography/Typography"
import { useNexusNavigate } from "../../hooks/useNexusNavigate"
import { useNexusProjectNumberOptional } from "../../hooks/useNexusParams"
import { useTitle } from "../../hooks/useTitle"
import { ProjectInfoShort } from "../../models/projects/ProjectInfoShort"
import { routes } from "../../routing/NexRoutes"

const projectStatuses: { [key: string]: string } = {
    A: "Active",
    I: "Inactive",
    D: "Dormant",
}

export const ProjectInit = () => {
    useTitle("Project Initialization")
    const nexusNav = useNexusNavigate()

    const projectNumber = useNexusProjectNumberOptional()

    const findAllNonAcquisitionProjectsResponse = useFindAllNonAcquisitionProjects()
    const nonAcqProjectList = findAllNonAcquisitionProjectsResponse.data ?? []
    const nonAcquisitionProjectResponse = useFetchNonAcquisitionProject(projectNumber)
    const selectedProject = nonAcqProjectList.find((s: ProjectInfoShort) => s.project_number === projectNumber)
    const initializeProjectMutation = useInitializeAcquisitionProject()

    const requestError =
        findAllNonAcquisitionProjectsResponse.error?.message ??
        nonAcquisitionProjectResponse.error?.message ??
        initializeProjectMutation.error?.message

    const projData = nonAcquisitionProjectResponse.data
    const projectFields: Detail[] = [
        { label: "Project Number", value: projData?.project_number },
        { label: "Project Name", value: projData?.project_short_name },
        { label: "Client Name", value: projData?.client_name },
        { label: "Practice Area", value: projData?.practice_area },
        { label: "Location", value: projData?.location },
        {
            label: "Account Manager",
            value: projData?.am_employee?.employee_full_name,
        },
        {
            label: "Project Manager",
            value: projData?.pm_employee?.employee_full_name,
        },
        {
            label: "Project Status",
            value: projData?.project_status && projectStatuses[projData.project_status],
        },
    ]

    const handleChange = (value: string | null) => {
        initializeProjectMutation.reset()
        const selectedProject = nonAcqProjectList.find((project) => project.project_number === value)
        nexusNav.initializeProject(selectedProject?.project_number)
    }

    const handleInitialization = () => {
        if (projectNumber == null) {
            throw new Error("Cannot initialize project when project number is not set.")
        }
        initializeProjectMutation.mutate(projectNumber, {
            onSuccess: () => nexusNav.editProject(projectNumber),
        })
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Grid>
                <Title>Acquisition Project Initialization</Title>
            </Grid>

            <Label gutterBottom="sm">Select Project to Initialize</Label>
            <NexAutocomplete
                TextFieldProps={{ placeholder: "Select..." }}
                value={selectedProject ?? null}
                options={nonAcqProjectList}
                getOptionLabel={(opt) => `${opt.project_number} - ${opt.project_long_name}`}
                onChange={(_, value) => handleChange(value?.project_number ?? null)}
                fullWidth
                loading={findAllNonAcquisitionProjectsResponse.isPending}
                disabled={findAllNonAcquisitionProjectsResponse.isPending}
                sx={{ mb: 2 }}
            />

            <Collapse in={nonAcquisitionProjectResponse.data != null}>
                <DetailsList label="Deltek Description of Selected Project" details={projectFields} />
            </Collapse>

            {requestError && <NexAlert animate>{requestError}</NexAlert>}

            <Grid container justifyContent="space-between" spacing={2} sx={{ mt: 2 }}>
                <Grid>
                    <Button component={Link} to={routes.home} variant="outlined" color="primary" size="large">
                        Back
                    </Button>
                </Grid>
                <Grid>
                    <LoadingButton
                        onClick={handleInitialization}
                        pending={initializeProjectMutation.isPending}
                        variant="contained"
                        color="primary"
                        size="large"
                        disabled={nonAcquisitionProjectResponse.data == null}
                    >
                        Initialize New Acquisition Project
                    </LoadingButton>
                </Grid>
            </Grid>
        </Container>
    )
}
